import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Clock, CheckCircle2, XCircle, Trophy, RotateCcw, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

export default function TakeQuiz({ user }) {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    loadQuiz();
  }, [quizId]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || result) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleSubmit(); // Auto-submit when time runs out
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeLeft, result]);

  const loadQuiz = async () => {
    try {
      const response = await fetch(`${API}/quizzes/${quizId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuiz(data);
        setAnswers(new Array(data.questions.length).fill(-1));
        
        // Set timer if quiz has time limit
        if (data.time_limit_minutes) {
          setTimeLeft(data.time_limit_minutes * 60);
        }
      } else {
        toast.error("Failed to load quiz");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Failed to load quiz");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (answerIndex) => {
    setAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[currentQuestion] = answerIndex;
      return newAnswers;
    });
  };

  const handleSubmit = useCallback(async () => {
    // Check if all questions are answered
    const unanswered = answers.filter(a => a === -1).length;
    if (unanswered > 0 && timeLeft !== 0) {
      const confirm = window.confirm(`You have ${unanswered} unanswered question(s). Submit anyway?`);
      if (!confirm) return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/quizzes/${quizId}/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ answers })
      });
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setTimeLeft(null); // Stop timer
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit quiz");
      }
    } catch (error) {
      toast.error("Failed to submit quiz");
    } finally {
      setSubmitting(false);
    }
  }, [answers, quizId, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <div className="text-[#3A5A40] text-lg">Loading quiz...</div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <Card className="p-8 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
          <p className="text-[#588157] mb-4">Quiz not found</p>
          <Button onClick={() => navigate(-1)} className="bg-[#3A5A40] text-white hover:bg-[#344E41]">
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  // Show results
  if (result) {
    return (
      <div className="min-h-screen bg-[#FAEDCD]">
        <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-xl font-bold text-[#344E41]">Quiz Results</h1>
          </div>
        </nav>

        <main className="max-w-3xl mx-auto p-4 sm:p-6 md:p-12">
          {/* Score Card */}
          <Card className={`p-8 text-center mb-8 rounded-2xl ${result.passed ? 'bg-[#588157]/10 border-[#588157]/30' : 'bg-[#D4A373]/10 border-[#D4A373]/30'}`}>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: result.passed ? '#588157' : '#D4A373' }}>
              {result.passed ? (
                <Trophy className="w-10 h-10 text-white" />
              ) : (
                <AlertCircle className="w-10 h-10 text-white" />
              )}
            </div>
            <h2 className="text-3xl font-bold mb-2" style={{ color: result.passed ? '#588157' : '#D4A373' }}>
              {result.score_percent}%
            </h2>
            <p className="text-lg text-[#344E41] mb-2">
              {result.earned_points} / {result.total_points} points
            </p>
            <p className={`text-lg font-medium ${result.passed ? 'text-[#588157]' : 'text-[#D4A373]'}`}>
              {result.message}
            </p>
            <p className="text-sm text-[#A3B18A] mt-2">
              Passing score: {result.passing_score}%
            </p>
          </Card>

          {/* Question Review */}
          <h3 className="text-lg font-semibold text-[#344E41] mb-4">Review Your Answers</h3>
          <div className="space-y-4">
            {result.results.map((r, index) => (
              <Card key={index} className={`p-4 rounded-xl ${r.is_correct ? 'bg-[#588157]/5 border-[#588157]/20' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${r.is_correct ? 'bg-[#588157]' : 'bg-red-500'}`}>
                    {r.is_correct ? (
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    ) : (
                      <XCircle className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-[#344E41] mb-2">
                      Q{index + 1}: {r.question}
                    </p>
                    <div className="text-sm space-y-1">
                      <p className={r.is_correct ? 'text-[#588157]' : 'text-red-600'}>
                        Your answer: {quiz.questions[index].options[r.selected_answer] || 'Not answered'}
                      </p>
                      {!r.is_correct && (
                        <p className="text-[#588157]">
                          Correct answer: {quiz.questions[index].options[r.correct_answer]}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-[#A3B18A] mt-1">
                      {r.points_earned} / {r.points_possible} points
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4 mt-8">
            <Button
              onClick={() => {
                setResult(null);
                setAnswers(new Array(quiz.questions.length).fill(-1));
                setCurrentQuestion(0);
                if (quiz.time_limit_minutes) {
                  setTimeLeft(quiz.time_limit_minutes * 60);
                }
              }}
              variant="outline"
              className="border-[#3A5A40] text-[#3A5A40] hover:bg-[#E9EDC9]"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Try Again
            </Button>
            <Button
              onClick={() => navigate(`/courses/${quiz.course_id}`)}
              className="bg-[#3A5A40] text-white hover:bg-[#344E41]"
            >
              Back to Course
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const question = quiz.questions[currentQuestion];
  const answeredCount = answers.filter(a => a !== -1).length;

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      {/* Header with timer */}
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-[#344E41]">{quiz.title}</h1>
            <p className="text-sm text-[#588157]">
              Question {currentQuestion + 1} of {quiz.questions.length}
            </p>
          </div>
          {timeLeft !== null && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${timeLeft < 60 ? 'bg-red-100 text-red-600' : 'bg-[#E9EDC9] text-[#3A5A40]'}`}>
              <Clock className="w-5 h-5" />
              <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 md:p-12">
        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-[#588157] mb-2">
            <span>{answeredCount} of {quiz.questions.length} answered</span>
            <span>{Math.round((answeredCount / quiz.questions.length) * 100)}%</span>
          </div>
          <div className="h-2 bg-[#E9EDC9] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#3A5A40] transition-all duration-300"
              style={{ width: `${(answeredCount / quiz.questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <Card className="p-6 sm:p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl mb-6">
          <p className="text-lg sm:text-xl font-medium text-[#344E41] mb-6">
            {question.question}
          </p>
          
          <div className="space-y-3">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                data-testid={`answer-option-${index}`}
                className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                  answers[currentQuestion] === index
                    ? 'bg-[#3A5A40] border-[#3A5A40] text-white'
                    : 'bg-white border-[#CCD5AE] hover:border-[#3A5A40] text-[#344E41]'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                    answers[currentQuestion] === index
                      ? 'bg-white/20 text-white'
                      : 'bg-[#E9EDC9] text-[#3A5A40]'
                  }`}>
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span>{option}</span>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            variant="outline"
            className="border-[#CCD5AE] text-[#588157] hover:bg-[#E9EDC9] disabled:opacity-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>
          
          {/* Question dots */}
          <div className="hidden sm:flex gap-1">
            {quiz.questions.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestion(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentQuestion
                    ? 'bg-[#3A5A40] scale-125'
                    : answers[index] !== -1
                    ? 'bg-[#588157]'
                    : 'bg-[#CCD5AE]'
                }`}
              />
            ))}
          </div>
          
          {currentQuestion < quiz.questions.length - 1 ? (
            <Button
              onClick={() => setCurrentQuestion(prev => Math.min(quiz.questions.length - 1, prev + 1))}
              className="bg-[#3A5A40] text-white hover:bg-[#344E41]"
            >
              Next <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#588157] text-white hover:bg-[#4a7049]"
              data-testid="submit-quiz-button"
            >
              {submitting ? 'Submitting...' : 'Submit Quiz'}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
